import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Zap, Clock, Target } from 'lucide-react';
import { Card } from './ui/Card';
interface ScoreEntry {
  rank: number;
  initials: string;
  value: string;
  detail?: string;
}
export function HighScores() {
  // Filler data for leaderboards
  const longestStreaks: ScoreEntry[] = [{
    rank: 1,
    initials: 'ACE',
    value: '47',
    detail: 'Addition'
  }, {
    rank: 2,
    initials: 'MAX',
    value: '42',
    detail: 'Multiplication'
  }, {
    rank: 3,
    initials: 'ZEN',
    value: '38',
    detail: 'Mixed'
  }, {
    rank: 4,
    initials: 'PRO',
    value: '35',
    detail: 'Division'
  }, {
    rank: 5,
    initials: 'SAM',
    value: '31',
    detail: 'Subtraction'
  }];
  const bestTimes: ScoreEntry[] = [{
    rank: 1,
    initials: 'FLY',
    value: '1:23',
    detail: '10 Problems'
  }, {
    rank: 2,
    initials: 'JET',
    value: '1:45',
    detail: '10 Problems'
  }, {
    rank: 3,
    initials: 'SKY',
    value: '3:12',
    detail: '25 Problems'
  }, {
    rank: 4,
    initials: 'ACE',
    value: '3:28',
    detail: '25 Problems'
  }, {
    rank: 5,
    initials: 'ZAP',
    value: '7:15',
    detail: '50 Problems'
  }];
  const topAccuracy: ScoreEntry[] = [{
    rank: 1,
    initials: 'PRO',
    value: '100%',
    detail: '25/25'
  }, {
    rank: 2,
    initials: 'ACE',
    value: '98%',
    detail: '49/50'
  }, {
    rank: 3,
    initials: 'MAX',
    value: '96%',
    detail: '24/25'
  }, {
    rank: 4,
    initials: 'ZEN',
    value: '95%',
    detail: '19/20'
  }, {
    rank: 5,
    initials: 'SAM',
    value: '94%',
    detail: '47/50'
  }];
  const renderLeaderboard = (title: string, icon: React.ReactNode, entries: ScoreEntry[], color: string) => <Card className="p-6 space-y-4" data-id="element-57">
      <div className="flex items-center gap-3 pb-3 border-b-2 border-slate-200" data-id="element-58">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`} data-id="element-59">
          {icon}
        </div>
        <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wide" data-id="element-60">
          {title}
        </h3>
      </div>

      <div className="space-y-2" data-id="element-61">
        {entries.map((entry, index) => <motion.div key={entry.rank} initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: index * 0.05
      }} className={`flex items-center justify-between p-3 rounded-lg transition-colors ${entry.rank === 1 ? 'bg-yellow-50 border-2 border-yellow-200' : entry.rank === 2 ? 'bg-slate-100 border-2 border-slate-300' : entry.rank === 3 ? 'bg-orange-50 border-2 border-orange-200' : 'bg-slate-50'}`} data-id="element-62">
            <div className="flex items-center gap-4" data-id="element-63">
              <div className={`w-8 h-8 rounded flex items-center justify-center font-bold text-sm ${entry.rank === 1 ? 'bg-yellow-400 text-yellow-900' : entry.rank === 2 ? 'bg-slate-400 text-slate-900' : entry.rank === 3 ? 'bg-orange-400 text-orange-900' : 'bg-slate-200 text-slate-600'}`} data-id="element-64">
                {entry.rank}
              </div>
              <div className="font-mono text-2xl font-bold text-slate-900 tracking-wider" data-id="element-65">
                {entry.initials}
              </div>
            </div>

            <div className="text-right" data-id="element-66">
              <div className="text-2xl font-bold text-slate-900 font-mono" data-id="element-67">
                {entry.value}
              </div>
              {entry.detail && <div className="text-xs text-slate-500 font-medium" data-id="element-68">
                  {entry.detail}
                </div>}
            </div>
          </motion.div>)}
      </div>
    </Card>;
  return <div className="w-full max-w-4xl mx-auto p-4 space-y-6 pb-20" data-id="element-69">
      {/* Hero Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-2 py-6" data-id="element-70">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 text-yellow-600 rounded-full mb-2" data-id="element-71">
          <Trophy className="w-8 h-8" data-id="element-72" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900" data-id="element-73">Hall of Fame</h2>
        <p className="text-slate-500" data-id="element-74">Top performers across all categories</p>
      </motion.div>

      {/* Leaderboards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-75">
        {renderLeaderboard('Longest Streaks', <Zap className="w-6 h-6 text-indigo-600" data-id="element-76" />, longestStreaks, 'bg-indigo-100')}

        {renderLeaderboard('Speed Records', <Clock className="w-6 h-6 text-emerald-600" data-id="element-77" />, bestTimes, 'bg-emerald-100')}

        {renderLeaderboard('Perfect Accuracy', <Target className="w-6 h-6 text-rose-600" data-id="element-78" />, topAccuracy, 'bg-rose-100')}
      </div>
    </div>;
}